import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, Container, Grid, Card, CardContent, IconButton, Avatar, CardMedia } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Menu from '@mui/icons-material/Menu';
import Book from '@mui/icons-material/Book';
import Person from '@mui/icons-material/Person';
import ChatBubble from '@mui/icons-material/ChatBubble';
import Mail from '@mui/icons-material/Mail';
import Phone from '@mui/icons-material/Phone';
import LocationOn from '@mui/icons-material/LocationOn';
import { useNavigate } from 'react-router-dom';

// Create a custom Material-UI theme with an orange palette
const theme = createTheme({
  palette: {
    primary: {
      main: '#FF9800', // A vibrant orange
    },
    secondary: {
      main: '#FFA726', // A slightly lighter orange/amber
    },
    background: {
      default: '#fffaf0', // Very light orange/cream background
      paper: '#ffffff', // White for cards and containers
    },
  },
  typography: {
    fontFamily: ['Inter', 'sans-serif'].join(','),
    h4: {
      fontWeight: 700,
      color: '#333',
    },
    h5: {
      fontWeight: 600,
      color: '#FF9800', // Orange for headings
    },
    body1: {
      color: '#555',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        },
      },
    },
  },
});

function Webpage() {
  const [currentPage, setCurrentPage] = useState('home'); // State to manage current page view
  const navigation = useNavigate();

  // Dummy data for tutors
  const tutors = [
    {
      id: 1,
      name: 'Teacher Anis',
      subject: 'Mathematics',
      quote: 'Making numbers fun and understandable!',
      image: 'https://placehold.co/150x150/FFA500/FFFFFF?text=Tutor+1', // Placeholder image
    },
    {
      id: 2,
      name: 'Sir Anwar',
      subject: 'English & Literature',
      quote: 'Unlocking the power of words.',
      image: 'https://placehold.co/150x150/FFA500/FFFFFF?text=Tutor+2',
    },
    {
      id: 3,
      name: 'Miss Erlia',
      subject: 'Bahasa Melayu',
      quote: 'Martabat bahasa, perkasa jiwa bangsa.',
      image: 'https://placehold.co/150x150/FFA500/FFFFFF?text=Tutor+3',
    },
    {
      id: 4,
      name: 'Cikgu Peja',
      subject: 'Sejarah',
      quote: 'Learning from the past, shaping the future.',
      image: 'https://placehold.co/150x150/FFA500/FFFFFF?text=Tutor+4',
    },
    {
      id: 5,
      name: 'Sir Salleh',
      subject: 'Physics',
      quote: 'Exploring the universe, one formula at a time.',
      image: 'https://placehold.co/150x150/FFA500/FFFFFF?text=Tutor+5',
    },
    {
      id: 6,
      name: 'Miss Nazihah',
      subject: 'Admin',
      quote: 'Pendidikan ialah pelaburahn masa depan anak kita.',
      image: 'https://placehold.co/150x150/FFA500/FFFFFF?text=Tutor+6',
    },
  ];

  const imageBg = require("./images/home/ptgm-background.jpeg");

  // Helper function to render different sections based on currentPage
  const renderContent = () => {
    switch (currentPage) {
      case 'home':
        return (
          <>
            {/* Hero Section */}
            <Box
              sx={{
                position: 'relative',
                background: 'linear-gradient(to right, #FF9800, #FFA726)', // orange-500 to amber-600
                color: 'white',
                py: { xs: 10, md: 20 }, // py-20 px-4 md:py-32
                px: 2,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundImage: `url(${imageBg})`,
                minHeight: '400px',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: 'black',
                  opacity: 0.5,
                  // borderRadius: '8px', // rounded-lg
                }}
              />
              <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '2.25rem', md: '3.75rem' }, color: "white", fontWeight: 700, mb: 4 }}>
                  Unlock Your Child's Full Potential
                </Typography>
                <Typography variant="h6" sx={{ fontSize: { xs: '1.125rem', md: '1.5rem' }, mb: 8 }}>
                  Personalized learning, expert tutors, and proven results.
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  onClick={() => setCurrentPage('contact')}
                  sx={{
                    px: 4, // px-8
                    py: 1.5, // py-3
                    backgroundColor: 'secondary.main',
                    '&:hover': {
                      backgroundColor: 'secondary.dark',
                      transform: 'scale(1.05)',
                    },
                    color: 'white',
                    borderRadius: '8px', // rounded-lg
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)', // shadow-lg
                    transition: 'transform 0.3s ease-in-out',
                  }}
                >
                  Enroll Today
                </Button>
              </Container>
            </Box>

            {/* About Us Section */}
            <Box id="about" sx={{ py: { xs: 8, md: 16 }, px: 2, bgcolor: 'background.paper' }}>
              <Container maxWidth="md" sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ mb: 8 }}>About Our Center</Typography>
                <Typography variant="body1" sx={{ fontSize: '1.125rem', lineHeight: '1.625' }}>
                  At Genius Milenia, we believe every student has the capacity to excel. Our dedicated team of experienced educators provides a supportive and stimulating environment where students can master challenging subjects, develop critical thinking skills, and build confidence. We offer a holistic approach to education, focusing on both academic achievement and personal growth.
                </Typography>
              </Container>
            </Box>

            {/* Services Section */}
            <Box id="services" sx={{ py: { xs: 8, md: 16 }, px: 2, bgcolor: 'grey.50' }}>
              <Container maxWidth="lg">
                <Typography variant="h4" sx={{ textAlign: 'center', mb: 12 }}>Our Core Programs</Typography>
                <Grid container spacing={4} justifyContent="center">
                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', p: 3 }}>
                        <Book sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                        <Typography variant="h5" sx={{ mb: 1 }}>Mathematics Mastery</Typography>
                        <Typography variant="body1" sx={{ textAlign: 'center' }}>
                          Comprehensive programs from elementary to advanced calculus. Build a strong foundation and conquer complex problems with ease.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', p: 3 }}>
                        <Person sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                        <Typography variant="h5" sx={{ mb: 1 }}>Science Explorers</Typography>
                        <Typography variant="body1" sx={{ textAlign: 'center' }}>
                          Engaging lessons in Physics, Chemistry, and Biology. Spark curiosity and deepen understanding through hands-on experiments.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', p: 3 }}>
                        <ChatBubble sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                        <Typography variant="h5" sx={{ mb: 1 }}>English & Language Arts</Typography>
                        <Typography variant="body1" sx={{ textAlign: 'center' }}>
                          Develop strong reading, writing, and communication skills. Boost vocabulary and ace those essays and exams.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Container>
            </Box>

            {/* Tutors Section - Now horizontally scrollable */}
            <Box sx={{ width: '100%', maxWidth: '6xl', mt: 6, mb: 4 }}> {/* w-full max-w-6xl mt-12 mb-8 */}
              <Typography variant="h4" component="h2" gutterBottom sx={{ color: 'black', textAlign: 'center', mb: 3 }}> {/* text-white text-center mb-6 */}
                Meet Our Expert Tutors
              </Typography>
              {/* Horizontally Scrollable Container for Tutor Cards */}
              <Box
                sx={{
                  display: 'flex',
                  overflowX: 'auto', // Enable horizontal scrolling
                  // scrollSnapType: 'x mandatory', // Smooth scrolling for cards
                  paddingY: 4,
                  paddingRight: 4,
                  paddingLeft: 4,
                  // --- Hide scrollbar ---
                  MsOverflowStyle: 'none', // IE and Edge
                  scrollbarWidth: 'none', // Firefox
                  '&::-webkit-scrollbar': {
                    display: 'none', // Chrome, Safari, Opera
                  },
                  gap: 3, // space-x-6 (approx equivalent)
                  pb: 4, // pb-4 (approx equivalent)
                }}
              >
                {tutors.map((tutor) => (
                  <Card
                    key={tutor.id}
                    sx={{
                      width: { xs: 250, sm: 300 }, // Responsive minimum width
                      flexShrink: 0, // Prevent cards from shrinking
                      scrollSnapAlign: 'start', // Align cards to start on scroll
                      boxShadow: 2, // shadow-lg (approx equivalent)
                      backgroundColor: 'white',
                      borderRadius: '1rem', // rounded-xl
                      overflow: 'hidden', // overflow-hidden
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="140"
                      image={tutor.image}
                      alt={tutor.name}
                      sx={{ width: '100%', minHeight: { xs: 250, sm: 300 }, objectFit: 'cover' }} // w-full h-40 object-cover
                    />
                    <CardContent sx={{ p: 2, textAlign: 'center' }}> {/* p-4 text-center */}
                      <Typography gutterBottom variant="h6" component="div" sx={{ fontWeight: 'semibold', color: 'text.primary' }}> {/* font-semibold text-gray-800 */}
                        {tutor.name}
                      </Typography>
                      <Typography variant="body1" color="primary" sx={{ mb: 1, fontWeight: 'medium' }}> {/* mb-2 font-medium */}
                        {tutor.subject}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        "{tutor.quote}"
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>

            {/* Testimonials Section */}
            <Box id="testimonials" sx={{ py: { xs: 8, md: 16 }, px: 2, bgcolor: 'grey.50' }}>
              <Container maxWidth="lg">
                <Typography variant="h4" sx={{ textAlign: 'center', mb: 12 }}>What Our Parents Say</Typography>
                <Grid container spacing={4} justifyContent="center">
                  <Grid item xs={12} md={6}>
                    <Card sx={{ p: 3 }}>
                      <CardContent>
                        <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 4 }}>
                          "My son's grades in Math have improved dramatically since joining Brilliance Academy. The tutors are incredibly patient and make learning fun!"
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'right' }}>- Sarah L.</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ p: 3 }}>
                      <CardContent>
                        <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 4 }}>
                          "The personalized attention my daughter receives here is unparalleled. She loves coming to her Science sessions!"
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'right' }}>- David K.</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Container>
            </Box>
          </>
        );
      case 'about':
        return (
          <Box sx={{ py: { xs: 8, md: 16 }, px: 2, bgcolor: 'background.paper', minHeight: 'calc(100vh - 64px - 100px)' }}>
            <Container maxWidth="md" sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ mb: 8 }}>About Genius Milenia</Typography>
              <Typography variant="body1" sx={{ fontSize: '1.125rem', lineHeight: '1.625', mb: 6 }}>
                Founded in 2018, Genius Milenia was established with a clear mission: to provide high-quality, personalized tuition that empowers students to achieve their academic goals and beyond. We understand that every student learns differently, which is why our programs are tailored to individual needs, learning styles, and paces.
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.125rem', lineHeight: '1.625', mb: 6 }}>
                Our team comprises highly qualified and passionate educators who are experts in their respective fields. They bring not only subject matter mastery but also a deep understanding of pedagogical techniques to foster a love for learning. We pride ourselves on creating a positive, encouraging, and challenging environment that inspires students to reach for excellence.
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.125rem', lineHeight: '1.625' }}>
                We are committed to nurturing not just academic success, but also developing critical thinking, problem-solving skills, and self-confidence essential for future success. Join the Brilliance Academy family and experience the difference personalized education can make!
              </Typography>
            </Container>
          </Box>
        );
      case 'services':
        return (
          <Box sx={{ py: { xs: 8, md: 16 }, px: 2, bgcolor: 'grey.50', minHeight: 'calc(100vh - 64px - 100px)' }}>
            <Container maxWidth="lg">
              <Typography variant="h4" sx={{ textAlign: 'center', mb: 12 }}>Our Comprehensive Services</Typography>
              <Grid container spacing={4} justifyContent="center">
                <Grid item xs={12} md={6} lg={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h5" sx={{ mb: 1.5, color: 'primary.main' }}>Individualized Tutoring</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        One-on-one sessions tailored to the student's specific academic needs and learning pace. Ideal for focused attention and deep understanding.
                      </Typography>
                      <Box component="ul" sx={{ listStyleType: 'disc', pl: 2, color: 'grey.700' }}>
                        <li>Customized lesson plans</li>
                        <li>Targeted subject assistance</li>
                        <li>Flexible scheduling</li>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h5" sx={{ mb: 1.5, color: 'primary.main' }}>Small Group Classes</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Learn collaboratively in a small group setting (max 5 students). Encourages peer learning and discussion while maintaining personalized feedback.
                      </Typography>
                      <Box component="ul" sx={{ listStyleType: 'disc', pl: 2, color: 'grey.700' }}>
                        <li>Interactive and dynamic sessions</li>
                        <li>Peer support and motivation</li>
                        <li>Cost-effective learning</li>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h5" sx={{ mb: 1.5, color: 'primary.main' }}>Exam Preparation Courses</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Intensive courses designed to prepare students for major examinations (e.g., SPM, IGCSE, SAT). Focus on exam techniques, past papers, and time management.
                      </Typography>
                      <Box component="ul" sx={{ listStyleType: 'disc', pl: 2, color: 'grey.700' }}>
                        <li>Strategic content review</li>
                        <li>Practice tests and mock exams</li>
                        <li>Stress management tips</li>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h5" sx={{ mb: 1.5, color: 'primary.main' }}>Holiday Programs</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Engaging and enriching programs during school holidays to catch up on difficult topics or get ahead for the next academic year.
                      </Typography>
                      <Box component="ul" sx={{ listStyleType: 'disc', pl: 2, color: 'grey.700' }}>
                        <li>Subject specific workshops</li>
                        <li>Revision and preview sessions</li>
                        <li>Fun and interactive activities</li>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Container>
          </Box>
        );
      case 'contact':
        return (
          <Box sx={{ py: { xs: 8, md: 16 }, px: 2, bgcolor: 'background.paper', minHeight: 'calc(100vh - 64px - 100px)' }}>
            <Container maxWidth="md" sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ mb: 8 }}>Contact Us</Typography>
              <Typography variant="body1" sx={{ fontSize: '1.125rem', lineHeight: '1.625', mb: 8 }}>
                Ready to take the next step? Get in touch with us today to discuss your child's educational needs or to schedule a consultation. We look forward to hearing from you!
              </Typography>
              <Grid container spacing={4} justifyContent="center">
                <Grid item xs={12} sm={6}>
                  <Card sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Mail sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>Email Us</Typography>
                    {/* Fixed: Used Typography component with 'component="a"' to apply sx prop to the link */}
                    <Typography variant="body1" component="a" href="mailto:info@brillianceacademy.com" sx={{ color: '#2563EB', textDecoration: 'underline' }}>geniusmileniaeducation@gmail.com</Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Phone sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>Call Us</Typography>
                    {/* Fixed: Used Typography component with 'component="a"' to apply sx prop to the link */}
                    <Typography variant="body1" component="a" href="tel:+60312345678" sx={{ color: '#2563EB', textDecoration: 'underline' }}>+6019-2888254</Typography>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <LocationOn sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>Visit Us</Typography>
                    <Typography variant="body1">
                      No 1, 1, Jalan PJS 5/30,<br />
                      Taman Medan, 46000 Petaling Jaya,<br />
                      Selangor
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Container>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <Box sx={{ flexGrow: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header/Navigation Bar */}
        <AppBar position="static" sx={{ bgcolor: 'primary.main', boxShadow: 3 }}>
          <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: { xs: 2, md: 4 }, py: 1.5 }}>
            <Typography onClick={() => setCurrentPage('home')} variant="h6" component="div" sx={{ fontWeight: 700, color: 'white', fontSize: { xs: '1.25rem', md: '1.5rem' }, cursor: "pointer" }}>
              Genius Milenia Education
            </Typography>
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Button color="inherit" onClick={() => setCurrentPage('home')} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }, px: 2, py: 1, borderRadius: '8px' }}>Home</Button>
              <Button color="inherit" onClick={() => setCurrentPage('about')} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }, px: 2, py: 1, borderRadius: '8px' }}>About</Button>
              <Button color="inherit" onClick={() => setCurrentPage('services')} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }, px: 2, py: 1, borderRadius: '8px' }}>Services</Button>
              <Button color="inherit" onClick={() => setCurrentPage('contact')} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }, px: 2, py: 1, borderRadius: '8px' }}>Contact</Button>
              <Button color="inherit" onClick={() => navigation('/')} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }, px: 2, py: 1, borderRadius: '8px' }}>Portal</Button>
            </Box>
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              sx={{ display: { xs: 'block', md: 'none' } }}
              onClick={() => console.log("Mobile menu button clicked. Implement a Drawer component here!")}
            >
              <Menu />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Main Content Area - Rendered conditionally based on currentPage */}
        <Box component="main" sx={{ flexGrow: 1 }}> {/* The main content area */}
          {renderContent()}
        </Box>

        {/* Footer */}
        <Box component="footer" sx={{ bgcolor: 'grey.800', color: 'white', py: 4, px: 2, mt: 'auto' }}>
          <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              © 2025 Genius Milenia Education. All rights reserved.
            </Typography>
            <Typography variant="body2">
              No 1, 1, Jalan PJS 5/30, Taman Medan, 46000 Petaling Jaya, Selangor
            </Typography>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default Webpage;
